// Pendências de quem sai da empresa (inativado ou excluído em Config → Usuários).
//
// Ao inativar ou excluir alguém, o sistema pergunta o que fazer com o que está
// aberto no nome da pessoa: transferir para alguém ativo ou só arquivar.
// "Arquivar" não grava nada: as tarefas de quem saiu já ficam fora das telas
// (src/lib/tarefaArquivada.ts) e as negociações continuam no nome dela.
//
// Lógica pura — as consultas e gravações ficam em pendenciasUsuarioDb.ts.

/**
 * Negociações que ainda são venda em andamento e mudam de dono na transferência.
 * Assinadas, em análise, aprovadas, perdidas e reprovadas ficam com quem vendeu:
 * mexer nelas mudaria de quem é a comissão e reescreveria o histórico.
 * Termo enviado entra — se o cliente assinar depois, a venda é do novo dono.
 */
export const STATUS_NEGOCIACAO_EM_ABERTO = ['em_andamento', 'simulacao_realizada', 'termo_enviado'] as const;

/** Tarefa (e visita) com esses status já terminou; o resto é pendência. */
export const STATUS_TAREFA_ENCERRADA = ['concluido', 'cancelado'] as const;

/** Solicitação da Central com esses status já terminou. */
export const STATUS_SOLICITACAO_ENCERRADA = ['concluido', 'rejeitado'] as const;

/**
 * - inativar: a pessoa perde o acesso e sai das listas, o histórico fica.
 * - excluir: apaga a conta — só passa sem vínculo nenhum (a edge manage-users recusa).
 * - transferir: só mexe nas pendências de quem já está inativo.
 */
export type AcaoSaida = 'inativar' | 'excluir' | 'transferir';

export type DestinoPendencias = 'transferir' | 'arquivar';

/** O que acontece com a conta da pessoa no fim. */
export type AcaoNaConta = 'inativar' | 'excluir' | 'nenhuma';

export interface Pendencias {
  /** Tarefas e visitas pendentes (categoria 'tarefa'). */
  tarefas: number;
  /** Negociações em aberto (STATUS_NEGOCIACAO_EM_ABERTO). */
  negociacoes: number;
  /** Solicitações abertas na Central — não entram na transferência, são redirecionadas por lá. */
  solicitacoes: number;
}

/** Os mesmos vínculos que a edge function manage-users confere antes de excluir. */
export interface VinculosExclusao {
  negociacoes: number;
  tarefas: number;
  notas: number;
  logsCrm: number;
  subordinados: number;
  comentarios: number;
  logsSistema: number;
}

/** O que a transferência leva (solicitação fica de fora). */
export const totalTransferivel = (p: Pendencias): number => p.tarefas + p.negociacoes;

/**
 * Vínculos que continuam no nome da pessoa depois da escolha. Transferir tira as
 * tarefas pendentes e as negociações em aberto; o histórico (concluídas, vendas
 * fechadas, notas, logs) sempre fica.
 */
export function vinculosQueSobram(
  v: VinculosExclusao,
  p: Pendencias,
  destino: DestinoPendencias,
): VinculosExclusao {
  if (destino !== 'transferir') return { ...v };
  return {
    ...v,
    negociacoes: Math.max(0, v.negociacoes - p.negociacoes),
    tarefas: Math.max(0, v.tarefas - p.tarefas),
  };
}

export const somaVinculos = (v: VinculosExclusao): number =>
  v.negociacoes + v.tarefas + v.notas + v.logsCrm + v.subordinados + v.comentarios + v.logsSistema;

/**
 * O que acontece com a conta no fim. Exclusão com vínculo vira inativação: a
 * edge recusaria excluir, e inativar já tira a pessoa do sistema sem apagar o
 * histórico de ninguém.
 */
export function acaoNaConta(acao: AcaoSaida, vinculosRestantes: number, jaInativo: boolean): AcaoNaConta {
  if (acao === 'transferir') return 'nenhuma';
  if (acao === 'excluir' && vinculosRestantes === 0) return 'excluir';
  return jaInativo ? 'nenhuma' : 'inativar';
}

/** Texto do botão de confirmar. */
export function rotuloConfirmar(destino: DestinoPendencias, conta: AcaoNaConta, temPendencias: boolean): string {
  const transfere = temPendencias && destino === 'transferir';
  const arquiva = temPendencias && destino === 'arquivar';
  if (conta === 'excluir') return transfere ? 'Transferir e excluir' : 'Excluir';
  if (conta === 'inativar') {
    if (transfere) return 'Transferir e inativar';
    if (arquiva) return 'Arquivar e inativar';
    return 'Inativar';
  }
  return transfere ? 'Transferir' : 'Arquivar';
}

const plural = (n: number, um: string, varios: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

/** Lista legível dos vínculos, só os que existem ("12 negociações", "3 notas"...). */
export function descreverVinculos(v: VinculosExclusao): string[] {
  const partes: string[] = [];
  if (v.negociacoes > 0) partes.push(plural(v.negociacoes, 'negociação', 'negociações'));
  if (v.tarefas > 0) partes.push(plural(v.tarefas, 'tarefa', 'tarefas'));
  if (v.notas > 0) partes.push(plural(v.notas, 'nota', 'notas'));
  if (v.logsCrm > 0) partes.push(plural(v.logsCrm, 'registro no CRM', 'registros no CRM'));
  if (v.subordinados > 0) partes.push(plural(v.subordinados, 'usuário vinculado', 'usuários vinculados'));
  if (v.comentarios > 0) partes.push(plural(v.comentarios, 'comentário', 'comentários'));
  if (v.logsSistema > 0) partes.push(plural(v.logsSistema, 'log do sistema', 'logs do sistema'));
  return partes;
}

export interface PerfilParaTransferencia {
  id: string;
  role?: string | null;
  status?: string | null;
  parceiro_id?: string | null;
}

/**
 * Quem pode receber as pendências: ativo, não é quem está saindo e está no
 * alcance de quem opera (o mesmo recorte do filtro de Responsável):
 * parceiro → a própria equipe; gerente de parcerias → parceiros e vendedores
 * parceiros; adm e gerente → qualquer pessoa ativa.
 */
export function podeReceberPendencias(
  ator: { id: string; role?: string | null },
  candidato: PerfilParaTransferencia,
  saindoId: string,
): boolean {
  if (candidato.status === 'inactive') return false;
  if (String(candidato.id) === String(saindoId)) return false;
  if (candidato.role === 'consumidor') return false;
  if (ator.role === 'parceiro') return candidato.id === ator.id || candidato.parceiro_id === ator.id;
  if (ator.role === 'gerente_parcerias') {
    return candidato.id === ator.id || candidato.role === 'parceiro' || candidato.role === 'vendedor_parceiro';
  }
  return true;
}
